import { useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import type { Session } from '@supabase/supabase-js'
import { getSupabaseClient, isSupabaseConfigured } from '../../lib/supabase'
import { getAppRedirectUrl } from '../../lib/platform'
import { AuthContext, type AuthContextValue } from './auth-context'

export function AuthProvider({ children }: PropsWithChildren) {
  const configured = isSupabaseConfigured()
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(configured)

  useEffect(() => {
    if (!configured) return
    const supabase = getSupabaseClient()
    void supabase.auth.getSession().then(({ data, error }) => {
      if (error) console.error('Unable to restore the authentication session.', error)
      setSession(data.session)
      setIsLoading(false)
    })
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => setSession(nextSession))
    return () => data.subscription.unsubscribe()
  }, [configured])

  const value = useMemo<AuthContextValue>(() => ({
    isConfigured: configured,
    isLoading,
    session,
    user: session?.user ?? null,
    signIn: async (email, password) => {
      const { error } = await getSupabaseClient().auth.signInWithPassword({ email, password })
      if (error) throw error
    },
    signUp: async (email, password, displayName, acquisitionIntent='customer') => {
      const { data, error } = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: { display_name: displayName.trim(), acquisition_intent: acquisitionIntent },
          emailRedirectTo: getAppRedirectUrl(`/setup?intent=${acquisitionIntent}` as `/${string}`),
        },
      })
      if (error) throw error
      return Boolean(data.session)
    },
    requestPasswordReset: async (email) => {
      const { error } = await getSupabaseClient().auth.resetPasswordForEmail(email, {
        redirectTo: getAppRedirectUrl('/reset-password'),
      })
      if (error) throw error
    },
    updatePassword: async (password) => {
      const { error } = await getSupabaseClient().auth.updateUser({ password })
      if (error) throw error
    },
    signOut: async () => {
      const { error } = await getSupabaseClient().auth.signOut()
      if (error) throw error
    },
  }), [configured, isLoading, session])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
